import {
  ThemeProvider,
  Typography,
  Box,
  Card,
  Icon,
  Divider,
  Link,
  StickyButton,
} from '@ace-clublabs/shared-component-library';
import Header from '../components/Header';
import { useGlobalState } from '../shared/state';
import { useNavigate } from 'react-router-dom';
import { getTermsUrl } from '../utils/methods';
import { useEffect } from 'react';
import { UtagHelper } from '../utils/utagHelper';
import { validatePBLExpiration } from '../utils/pblExpirationHelper';

export const PayByLink = () => {
  const navigate = useNavigate();
  const [payByLinkData] = useGlobalState('payByLinkData');
  const [manifestId] = useGlobalState('manifestId');
  const [customerId] = useGlobalState('customerId');
  const [sessionId] = useGlobalState('sessionId');
  const [, setError] = useGlobalState('error');

  const { products, total, hasAutoPayEnrollment } = payByLinkData || {};

  useEffect(() => {
    validatePBLExpiration(payByLinkData, navigate, setError);
  }, [payByLinkData, navigate, setError]);

  useEffect(() => {
    if (customerId && sessionId) {
      const smsReviewTags = {
        channel: 'my account',
        custID: customerId,
        page: 'my account:sms:review',
        sessionid: sessionId,
        subchannel1: 'sms',
      };

      UtagHelper.tag(smsReviewTags);
    }
  }, [customerId, sessionId]);

  const getPolicyCardInfo = (productSubType: string | undefined) => {
    switch (productSubType?.toLowerCase()) {
      case 'classic':
        return { policyIcon: <Icon iconName="card-membership-solid" color="primary" />, title: 'Classic member' };
      case 'classic rv':
        return {
          policyIcon: <Icon iconName="card-membership-solid" color="primary" />,
          title: 'Classic RV member',
        };
      case 'plus':
        return {
          policyIcon: <Icon iconName="card-membership-solid" color="primary" />,
          title: (
            <span>
              Plus<sup>®</sup> member
            </span>
          ),
        };
      case 'plus rv':
        return {
          policyIcon: <Icon iconName="card-membership-solid" color="primary" />,
          title: (
            <span>
              Plus<sup>®</sup> RV member
            </span>
          ),
        };
      case 'premier':
        return {
          policyIcon: <Icon iconName="card-membership-solid" color="primary" />,
          title: (
            <span>
              Premier<sup>®</sup> member
            </span>
          ),
        };
      case 'premier rv':
        return {
          policyIcon: <Icon iconName="card-membership-solid" color="primary" />,
          title: (
            <span>
              Premier<sup>®</sup> RV member
            </span>
          ),
        };
      case 'auto':
        return { policyIcon: <Icon iconName="car-duotone" htmlColor="#3F5F90" />, title: 'Auto policy' };
      case 'collector':
        return { policyIcon: <Icon iconName="car-duotone" htmlColor="#3F5F90" />, title: 'Collector car policy' };
      case 'home':
        return { policyIcon: <Icon iconName="house-chimney-duotone" htmlColor="#933BE2" />, title: 'Home policy' };
      case 'condo':
        return { policyIcon: <Icon iconName="apartment-duotone" htmlColor="#933BE2" />, title: 'Condo policy' };
      case 'renters':
        return { policyIcon: <Icon iconName="renters-duotone" htmlColor="#933BE2" />, title: 'Renters policy' };
      case 'tenant':
        return { policyIcon: <Icon iconName="renters-duotone" htmlColor="#933BE2" />, title: 'Tenant policy' };
      case 'dwelling':
        return {
          policyIcon: <Icon iconName="renters-duotone" htmlColor="#933BE2" />,
          title: 'Rental dwelling property policy',
        };
      case 'earthquake':
        return {
          policyIcon: <Icon iconName="house-crack-duotone" htmlColor="#933BE2" />,
          title: 'CA earthquake authority policy',
        };
      case 'pup':
        return {
          policyIcon: <Icon iconName="umbrella-simple-duotone" htmlColor="#D97706" />,
          title: 'Personal umbrella policy',
        };
      case 'watercraft':
        return { policyIcon: <Icon iconName="sailboat-duotone" htmlColor="#3F5F90" />, title: 'Watercraft policy' };
      default:
        return {
          policyIcon: <Icon iconName="id-card2-duotone" color="primary" />,
          title: `${capitalize(productSubType)} policy`,
        };
    }
  };

  const capitalize = (value: string | undefined) => {
    if (!value || value.length === 0) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const mappedPaymentCards = products?.map((product: any) => {
    const {
      amount,
      autoPay,
      discount,
      productId,
      productSubType,
      productType,
      total: productTotal,
      ersAmount,
      ersText,
      productText,
    } = product;
    const isMembership = productType.toLowerCase() === 'membership';
    const isErs = productType.toLowerCase() === 'ers';
    const formattedProductId =
      isMembership || isErs
        ? `${productId?.substring(0, 3)} ${productId?.substring(3, 6)} ${productId?.substring(6)}`
        : productId;
    return (
      <Card className={`pay-by-link-card`}>
        <Box className={`pay-by-link-box-title`}>
          {getPolicyCardInfo(productSubType).policyIcon}
          <Typography variant="h6Medium" component="h2">
            {getPolicyCardInfo(productSubType).title}
          </Typography>
        </Box>

        <Box className={`pay-by-link-line-items product-id-${productSubType}`}>
          <Typography variant="b1Regular">{isMembership || isErs ? 'Membership #' : 'Policy #'}</Typography>
          <Typography variant="b1Regular" className="qm-mask">
            {formattedProductId}
          </Typography>
        </Box>

        {!!amount && (
          <Box className={`pay-by-link-line-items min-due-${productSubType}`}>
            <Typography variant="b1Regular">{productText}</Typography>
            <Typography variant="b1Regular">${amount.toFixed(2)}</Typography>
          </Box>
        )}

        {!!ersAmount && (
          <Box className={`pay-by-link-line-items ers-due`}>
            <Typography variant="b1Regular">{ersText}</Typography>
            <Typography variant="b1Regular">${ersAmount.toFixed(2)}</Typography>
          </Box>
        )}

        {!!discount && (
          <>
            <Box className="discount-container">
              <Box className="savings-container">
                <Typography variant="b1Regular">
                  <Icon iconName="tag-solid" />
                </Typography>
                <Typography variant="b1Regular">Auto Pay savings</Typography>
              </Box>
              <Typography variant="b1Regular" className="discount-amount">
                {`-$${discount.toFixed(2)}`}
              </Typography>
            </Box>
            <Typography variant="b3Regular" component="div" data-quid="discount-text">
              Auto Pay enrollment is required for discount
            </Typography>
          </>
        )}

        {autoPay && (
          <Box className="auto-pay-enrollment-box">
            <Typography variant="b2Regular">
              <strong>Enrolling in Auto Pay</strong> — Select a payment method on the next screen that will be used for
              Auto Pay.
            </Typography>
          </Box>
        )}

        {!!productTotal && (
          <>
            <Divider />
            <Box className={`pay-by-link-line-items total-${productSubType}`}>
              <Typography variant="b1Bold">Total</Typography>
              <Typography variant="b1Bold">${productTotal.toFixed(2)}</Typography>
            </Box>
          </>
        )}
      </Card>
    );
  });

  const hasPayment = total && total > 0;
  const autoPayEnrollmentOnly = hasAutoPayEnrollment && !hasPayment;
  const formTitle = autoPayEnrollmentOnly ? 'Enroll in AutoPay' : 'Make a payment';

  return (
    <Box>
      <Header />
      <ThemeProvider>
        <Box id="pay-by-link-outer-container">
          <Box id="pay-by-link-inner-container">
            <Typography variant="h5Medium" component="h1">
              {formTitle}
            </Typography>
            <Typography variant="b2Regular" id="pay-by-link-subtitle">
              The items below were selected by your agent. Please review and proceed.
            </Typography>
            {mappedPaymentCards}
            {hasAutoPayEnrollment && (
              <Typography variant="b2Regular" component="div" className="terms-and-conditions-text">
                By clicking Enroll and continue, I hereby enroll in Auto Pay and have read and agree to the{' '}
                <Link
                  data-id="terms-conditions-link"
                  id="terms-conditions-link"
                  variant="b2Regular"
                  href={getTermsUrl()}
                  target="_blank"
                  sx={{ verticalAlign: 'baseline', color: '#0156A3', textDecorationColor: '#0156A3' }}
                >
                  Terms & Conditions
                </Link>
                .
              </Typography>
            )}
          </Box>
          <StickyButton
            id={total && total > 0 ? 'submit-button' : 'continue-button'}
            slotProps={{
              primaryButton: {
                label: hasAutoPayEnrollment ? 'Enroll and continue' : 'Continue',
                onClick: () => {
                  navigate(`/${manifestId}`);
                },
                disabled: false,
                endIcon: total && total > 0 ? <Icon iconName="lock-keyhole-solid" /> : undefined,
              },
            }}
            renderAmount={total && total > 0 ? total : undefined}
          />
        </Box>
      </ThemeProvider>
    </Box>
  );
};
